<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import sortBy from "lodash/sortBy";
import isEmpty from "lodash/isEmpty";
import TextLabel from "@/components/UI/TextLabel.vue";

const store = useStore();
const i18n = window.measurementSelectorVariables.i18n;

const currentThickness = computed(() => store.getters.currentProductThickness);
const thicknesses = computed(() => sortBy( [ currentThickness.value, ...store.getters.alternateThicknesses ], [ 'thickness' ] ) );
const baseShapeInstanceModel = computed(() => store.getters.baseShapeInstance.model);

function isActive( thickness: number ): boolean {
	return currentThickness.value.thickness === thickness;
}
function getUrl( item: { thickness: number; permalink: string; } ): string {
	if ( isActive( item.thickness ) ) {
		return '#';
	}

	return item.permalink;
}
const generateTemporaryLink = async ( $event: { target: { href: string; }; } ): Promise<string> => {
	if ( ! isEmpty( baseShapeInstanceModel.value ) ) {
		location.href = await store.dispatch( 'generateShareableLink', { url: $event.target.href, isTemporary: true } );

		return;
	}

	location.href = $event.target.href;
};
</script>
<template>
	<div class="thickness-selector">
		<text-label class="subtitle">
			{{ i18n.thickness_in_mm }}
		</text-label>
		<ul class="thickness-selector__options">
			<li :class="{
				'thickness-selector__item': true,
				'thickness-selector__item--active': isActive( item.thickness ),
			}" v-for="(item, index) in thicknesses" :key="index">
				<a v-if="!isActive( item.thickness )"
					@click.prevent="generateTemporaryLink"
					:href="getUrl( item )"
					class="thickness-selector__button"
					:aria-label="`${item.thickness}mm`"
				>
					{{ item.thickness }}
				</a>
				<a  v-else
					href="#"
					class="thickness-selector__button thickness-selector__button--active"
					:aria-label="`${item.thickness}mm`"
				>
					{{ item.thickness }}
				</a>
			</li>
		</ul>
	</div>
</template>
<style scoped lang="scss">
.subtitle {
	color: $brand-blue-dark;
	margin-bottom: 5px;
}
.thickness-selector__options {
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
}
.thickness-selector__item {
	text-align: center;
	border: 1px solid $border-color-blue;
	position: relative;
	z-index: 1;

	+ * {
		margin-inline-start: -1px;
	}

	&:first-child {
		border-start-start-radius: 4px;
		border-end-start-radius: 4px;
	}

	&:last-child {
		border-start-end-radius: 4px;
		border-end-end-radius: 4px;
	}

	&--active {
		background-color: $measurements-light-blue;
		border-color: $measurements-preview-blue;
		z-index: 2;
	}

	&:hover, &:focus {
		border-color: $measurements-preview-blue;
		z-index: 2;
	}
}
.thickness-selector__button {
	display: block;
	line-height: 2rem;
	height: 2rem;

	&:hover, &:focus {
		text-decoration: underline;
	}

	&--active {
		font-weight: 700;
		cursor: default;

		&:hover, &:focus {
			text-decoration: none;
		}
	}
}
</style>
